import React, { useState, useEffect } from 'react';
import { 
  X, Plus, Trash2, Clock, DollarSign, Users, BookOpen, CheckCircle, 
  Calendar, Settings, ChevronLeft, ChevronRight, AlertCircle 
} from 'lucide-react';
import { classService } from '../../services/classService';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' }
];

const BILLING_FREQUENCIES = [
  { value: 'one_time', label: 'One Time' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semester', label: 'Semester' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'per_session', label: 'Per Session' }
];

const TABS = [
  { id: 'basic', label: 'Basic Info', icon: BookOpen },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'attendance', label: 'Attendance', icon: CheckCircle },
  { id: 'payments', label: 'Payment Plans', icon: DollarSign },
  { id: 'prerequisites', label: 'Prerequisites', icon: Users },
  { id: 'custom', label: 'Custom Fields', icon: Settings }
];

const INITIAL_FORM_DATA = {
  basicInfo: {
    name: '',
    description: '',
    classTypeId: '',
    instructorId: '',
    capacity: 20,
    minAge: null,
    maxAge: null,
    location: '',
    status: 'active',
    selectedSubjects: []
  },
  scheduleDays: [],
  attendanceRules: {
    defaultCheckInWindow: 15,
    defaultCheckOutWindow: 15,
    defaultLateThreshold: 10,
    markAbsentAfterMinutes: 30,
    excusedAbsenceRequiresNote: true,
    allowEarlyCheckout: true,
    earlyCheckoutRequiresApproval: false,
    notifyParentOnCheckIn: false,
    notifyParentOnCheckOut: true,
    notifyParentOnAbsence: true,
    notifyParentOnLate: true,
    allowQrCheckIn: true,
    allowStaffCheckIn: true,
    allowParentAppCheckIn: true,
    requirePhotoOnCheckIn: false
  },
  paymentPlans: [],
  prerequisites: [],
  customFields: {}
};

/**
 * ClassForm Component
 * Multi-tab form for creating and editing classes with full customization
 */
const ClassForm = ({ 
  classItem = null, 
  onSubmit, 
  onClose, 
  classTypes = [], 
  subjects = [], 
  availableClasses = [],
  customFieldDefs = [],
  isLoading = false 
}) => {
  const isEditing = !!classItem?.id;
  const [activeTab, setActiveTab] = useState('basic');
  const [errors, setErrors] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [_touched, setTouched] = useState({});
  
  // Unified form data state
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  // Initialize form with existing data when editing
  useEffect(() => {
    if (classItem) {
      setFormData({
        basicInfo: {
          name: classItem.name || '',
          description: classItem.description || '',
          classTypeId: classItem.class_type_id || classItem.classTypeId || '',
          instructorId: classItem.instructor_id || classItem.instructorId || '',
          capacity: classItem.capacity || 20,
          minAge: classItem.min_age || classItem.minAge || null,
          maxAge: classItem.max_age || classItem.maxAge || null,
          location: classItem.location || '',
          status: classItem.status || 'active',
          selectedSubjects: classItem.subjects?.map(s => s.id) || 
                           classItem.subject_ids || []
        },
        scheduleDays: classItem.schedule_days || 
                      classItem.scheduleDays || 
                      (Array.isArray(classItem.schedule) ? classItem.schedule.map((s, i) => ({
                        dayOfWeek: DAYS_OF_WEEK.find(d => d.label === s.day)?.value || i % 7,
                        startTime: s.startTime || '09:00',
                        endTime: s.endTime || '10:00',
                        checkInWindowMinutes: s.checkInWindowMinutes || 15,
                        checkOutWindowMinutes: s.checkOutWindowMinutes || 15,
                        lateThresholdMinutes: s.lateThresholdMinutes || 10,
                        room: s.room || '',
                        isActive: true
                      })) : []),
        attendanceRules: {
          ...INITIAL_FORM_DATA.attendanceRules,
          ...((classItem.attendance_rules || classItem.attendanceRules) && 
             typeof (classItem.attendance_rules || classItem.attendanceRules) === 'object'
            ? (classItem.attendance_rules || classItem.attendanceRules)
            : {})
        },
        paymentPlans: classItem.payment_plans || 
                      classItem.paymentPlans || [],
        prerequisites: classItem.prerequisites || 
                       classItem.prerequisite_classes?.map(p => ({
                         prerequisiteClassId: p.id,
                         isRequired: p.is_required ?? true,
                         notes: p.notes || ''
                       })) || [],
        customFields: classItem.custom_fields || 
                      classItem.customFields || {}
      });
    }
  }, [classItem]);

  // Update nested form data helper
  const updateFormSection = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: typeof data === 'function' ? data(prev[section]) : data
    }));
    // Clear errors for this section
    if (errors[section]) {
      setErrors(prev => ({ ...prev, [section]: undefined }));
    }
  };

  // Schedule Day Handlers
  const handleAddScheduleDay = () => {
    updateFormSection('scheduleDays', prev => [
      ...prev, 
      {
        dayOfWeek: 1,
        startTime: '09:00',
        endTime: '10:00',
        checkInWindowMinutes: formData.attendanceRules.defaultCheckInWindow,
        checkOutWindowMinutes: formData.attendanceRules.defaultCheckOutWindow,
        lateThresholdMinutes: formData.attendanceRules.defaultLateThreshold,
        room: '',
        isActive: true
      }
    ]);
  };

  const handleRemoveScheduleDay = (index) => {
    updateFormSection('scheduleDays', prev => prev.filter((_, i) => i !== index));
  };

  const handleScheduleDayChange = (index, field, value) => {
    updateFormSection('scheduleDays', prev => {
      const newDays = [...prev];
      newDays[index] = { ...newDays[index], [field]: value };
      return newDays;
    });
  };

  // Payment Plan Handlers
  const handleAddPaymentPlan = () => {
    updateFormSection('paymentPlans', prev => [
      ...prev,
      {
        planName: '',
        description: '',
        amount: 0,
        billingFrequency: 'monthly',
        planType: 'standard',
        siblingDiscountPercent: 0,
        earlyBirdDiscountPercent: 0,
        firstPaymentDue: 'on_enrollment',
        refundPolicy: '',
        isActive: true,
        isDefault: prev.length === 0 // First plan is default
      }
    ]);
  };

  const handleRemovePaymentPlan = (index) => {
    updateFormSection('paymentPlans', prev => {
      const newPlans = prev.filter((_, i) => i !== index);
      // Ensure at least one default if plans remain
      if (newPlans.length > 0 && !newPlans.some(p => p.isDefault)) {
        newPlans[0].isDefault = true;
      }
      return newPlans;
    });
  };

  const handlePaymentPlanChange = (index, field, value) => {
    updateFormSection('paymentPlans', prev => {
      const newPlans = [...prev];
      newPlans[index] = { ...newPlans[index], [field]: value };
      
      // Handle default flag - only one default allowed
      if (field === 'isDefault' && value === true) {
        newPlans.forEach((plan, i) => {
          if (i !== index) plan.isDefault = false;
        });
      }
      
      return newPlans;
    });
  };

  // Prerequisite Handlers
  const handleAddPrerequisite = () => {
    updateFormSection('prerequisites', prev => [
      ...prev,
      {
        prerequisiteClassId: '',
        isRequired: true,
        notes: ''
      }
    ]);
  };

  const handleRemovePrerequisite = (index) => {
    updateFormSection('prerequisites', prev => prev.filter((_, i) => i !== index));
  };

  const handlePrerequisiteChange = (index, field, value) => {
    updateFormSection('prerequisites', prev => {
      const newPrereqs = [...prev];
      newPrereqs[index] = { ...newPrereqs[index], [field]: value };
      return newPrereqs;
    });
  };

  // Custom Field Handlers
  const handleCustomFieldChange = (fieldId, value) => {
    updateFormSection('customFields', prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  // Validation
  const validateBasicInfo = () => {
    const errors = {};
    if (!formData.basicInfo.name.trim()) {
      errors.name = 'Class name is required';
    }
    if (formData.basicInfo.capacity < 1) {
      errors.capacity = 'Capacity must be at least 1';
    }
    if (formData.basicInfo.minAge && formData.basicInfo.maxAge && 
        formData.basicInfo.minAge > formData.basicInfo.maxAge) {
      errors.minAge = 'Min age cannot be greater than max age';
    }
    return errors;
  };

  const validateSchedule = () => {
    const errors = [];
    formData.scheduleDays.forEach((day, index) => {
      if (day.startTime >= day.endTime) {
        errors.push(`Day ${index + 1}: End time must be after start time`);
      }
    });
    return errors;
  };

  const validatePaymentPlans = () => {
    const errors = [];
    formData.paymentPlans.forEach((plan, index) => {
      if (!plan.planName.trim()) {
        errors.push(`Plan ${index + 1}: Name is required`);
      }
      if (plan.amount < 0) {
        errors.push(`Plan ${index + 1}: Amount cannot be negative`);
      }
    });
    return errors;
  };

  const validateForm = () => {
    const basicErrors = validateBasicInfo();
    const scheduleErrors = validateSchedule();
    const paymentErrors = validatePaymentPlans();

    const allErrors = {
      basic: Object.keys(basicErrors).length > 0 ? basicErrors : null,
      schedule: scheduleErrors.length > 0 ? scheduleErrors : null,
      payments: paymentErrors.length > 0 ? paymentErrors : null
    };

    setErrors(allErrors);
    return !Object.values(allErrors).some(e => e !== null);
  };

  // Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error tab
      if (errors.basic) setActiveTab('basic');
      else if (errors.schedule) setActiveTab('schedule');
      else if (errors.payments) setActiveTab('payments');
      return;
    }

    const submitData = {
      ...formData.basicInfo,
      scheduleDays: formData.scheduleDays,
      attendanceRules: formData.attendanceRules,
      paymentPlans: formData.paymentPlans,
      prerequisites: formData.prerequisites,
      customFields: formData.customFields
    };

    try {
      if (isEditing && classItem?.id) {
        await classService.update(classItem.id, submitData);
      } else {
        await classService.create(submitData);
      }
      onSubmit(submitData);
    } catch (err) {
      console.error('Failed to save class:', err);
      setErrors(prev => ({
        ...prev,
        submit: err.message || 'Failed to save class. Please try again.'
      }));
    }
  };

  // Navigation between tabs
  const goToNextTab = () => {
    const currentIndex = TABS.findIndex(t => t.id === activeTab);
    if (currentIndex < TABS.length - 1) {
      setActiveTab(TABS[currentIndex + 1].id);
    }
  };

  const goToPrevTab = () => {
    const currentIndex = TABS.findIndex(t => t.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(TABS[currentIndex - 1].id);
    }
  };

  // Render Tab Content
  const renderBasicInfo = () => (
    <div className="space-y-5 animate-fade-in">
      <div>
        <label className="form-label flex items-center">
          Class Name 
          <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="text"
          className={`form-input ${errors.basic?.name ? 'border-red-500 focus:border-red-500' : ''}`}
          value={formData.basicInfo.name}
          onChange={(e) => updateFormSection('basicInfo', { ...formData.basicInfo, name: e.target.value })}
          onBlur={() => setTouched(prev => ({ ...prev, name: true }))}
          placeholder="e.g., Ballet Beginners"
        />
        {errors.basic?.name && (
          <p className="text-red-500 text-sm mt-1 flex items-center">
            <AlertCircle size={14} className="mr-1" />
            {errors.basic.name}
          </p>
        )}
      </div>

      <div>
        <label className="form-label">Description</label>
        <textarea
          className="form-input"
          rows="3"
          value={formData.basicInfo.description}
          onChange={(e) => updateFormSection('basicInfo', { ...formData.basicInfo, description: e.target.value })}
          placeholder="Describe the class, what students will learn, etc."
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="form-label">Class Type</label>
          <select
            className="form-input"
            value={formData.basicInfo.classTypeId}
            onChange={(e) => updateFormSection('basicInfo', { ...formData.basicInfo, classTypeId: e.target.value })}
          >
            <option value="">Select type...</option>
            {classTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="form-label">Status</label>
          <select
            className="form-input"
            value={formData.basicInfo.status}
            onChange={(e) => updateFormSection('basicInfo', { ...formData.basicInfo, status: e.target.value })}
          >
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="inactive">Inactive</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="form-label">Capacity</label>
          <input
            type="number"
            className={`form-input ${errors.basic?.capacity ? 'border-red-500' : ''}`}
            value={formData.basicInfo.capacity}
            onChange={(e) => updateFormSection('basicInfo', { ...formData.basicInfo, capacity: parseInt(e.target.value) || 1 })}
            min="1"
          />
          {errors.basic?.capacity && (
            <p className="text-red-500 text-sm mt-1">{errors.basic.capacity}</p>
          )}
        </div>

        <div>
          <label className="form-label">Min Age</label>
          <input
            type="number"
            className={`form-input ${errors.basic?.minAge ? 'border-red-500' : ''}`}
            value={formData.basicInfo.minAge || ''}
            onChange={(e) => updateFormSection('basicInfo', { ...formData.basicInfo, minAge: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="Optional"
          />
        </div>

        <div>
          <label className="form-label">Max Age</label>
          <input
            type="number"
            className="form-input"
            value={formData.basicInfo.maxAge || ''}
            onChange={(e) => updateFormSection('basicInfo', { ...formData.basicInfo, maxAge: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="Optional"
          />
        </div>
      </div>

      <div>
        <label className="form-label">Location/Room</label>
        <input
          type="text"
          className="form-input"
          value={formData.basicInfo.location}
          onChange={(e) => updateFormSection('basicInfo', { ...formData.basicInfo, location: e.target.value })}
          placeholder="e.g., Studio A, Room 101"
        />
      </div>

      {subjects.length > 0 && (
        <div>
          <label className="form-label">Subjects/Tags</label>
          <p className="text-sm text-gray-500 mb-2">Select all that apply</p>
          <div className="flex flex-wrap gap-2">
            {subjects.map(subject => {
              const isSelected = formData.basicInfo.selectedSubjects.includes(subject.id);
              return (
                <button
                  key={subject.id}
                  type="button"
                  onClick={() => {
                    updateFormSection('basicInfo', {
                      ...formData.basicInfo,
                      selectedSubjects: isSelected
                        ? formData.basicInfo.selectedSubjects.filter(id => id !== subject.id)
                        : [...formData.basicInfo.selectedSubjects, subject.id]
                    });
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    isSelected
                      ? 'ring-2 ring-offset-1'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={isSelected ? {
                    backgroundColor: subject.color + '20',
                    color: subject.color,
                    borderColor: subject.color,
                    ringColor: subject.color
                  } : {}}
                >
                  {subject.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const renderSchedule = () => (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Class Schedule</h3>
          <p className="text-sm text-gray-500">
            Add the days and times this class meets
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddScheduleDay}
          className="btn btn-secondary flex items-center text-sm"
        >
          <Plus size={16} className="mr-1" />
          Add Day
        </button>
      </div>

      {errors.schedule && errors.schedule.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          {errors.schedule.map((err, i) => (
            <p key={i} className="text-red-600 text-sm flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {err}
            </p>
          ))}
        </div>
      )}

      {formData.scheduleDays.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Clock className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="font-medium">No schedule days added yet</p>
          <p className="text-sm mt-1 mb-3">Add days when this class meets</p>
          <button
            type="button"
            onClick={handleAddScheduleDay}
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            Add your first day
          </button>
        </div>
      )}

      <div className="space-y-4">
        {formData.scheduleDays.map((day, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </span>
                <span className="font-medium text-gray-900">
                  {DAYS_OF_WEEK.find(d => d.value === day.dayOfWeek)?.label}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveScheduleDay(index)}
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Day</label>
                <select
                  className="form-input text-sm"
                  value={day.dayOfWeek}
                  onChange={(e) => handleScheduleDayChange(index, 'dayOfWeek', parseInt(e.target.value))}
                >
                  {DAYS_OF_WEEK.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Start Time</label>
                <input
                  type="time"
                  className="form-input text-sm"
                  value={day.startTime}
                  onChange={(e) => handleScheduleDayChange(index, 'startTime', e.target.value)}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">End Time</label>
                <input
                  type="time"
                  className="form-input text-sm"
                  value={day.endTime}
                  onChange={(e) => handleScheduleDayChange(index, 'endTime', e.target.value)}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Room</label>
                <input
                  type="text"
                  className="form-input text-sm"
                  value={day.room}
                  onChange={(e) => handleScheduleDayChange(index, 'room', e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-200">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Check-in Window (min)</label>
                <input
                  type="number"
                  className="form-input text-sm"
                  value={day.checkInWindowMinutes}
                  onChange={(e) => handleScheduleDayChange(index, 'checkInWindowMinutes', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Check-out Window (min)</label>
                <input
                  type="number"
                  className="form-input text-sm"
                  value={day.checkOutWindowMinutes}
                  onChange={(e) => handleScheduleDayChange(index, 'checkOutWindowMinutes', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Late Threshold (min)</label>
                <input
                  type="number"
                  className="form-input text-sm"
                  value={day.lateThresholdMinutes}
                  onChange={(e) => handleScheduleDayChange(index, 'lateThresholdMinutes', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAttendance = () => (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h3 className="font-medium text-gray-900 mb-1">Attendance Settings</h3>
        <p className="text-sm text-gray-500">Configure how attendance is tracked for this class</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="form-label text-sm">Default Check-in Window</label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              className="form-input w-24"
              value={formData.attendanceRules.defaultCheckInWindow}
              onChange={(e) => updateFormSection('attendanceRules', { 
                ...formData.attendanceRules, 
                defaultCheckInWindow: parseInt(e.target.value) || 0 
              })}
              min="0"
            />
            <span className="text-gray-600">minutes before class</span>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="form-label text-sm">Default Check-out Window</label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              className="form-input w-24"
              value={formData.attendanceRules.defaultCheckOutWindow}
              onChange={(e) => updateFormSection('attendanceRules', { 
                ...formData.attendanceRules, 
                defaultCheckOutWindow: parseInt(e.target.value) || 0 
              })}
              min="0"
            />
            <span className="text-gray-600">minutes after class</span>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="form-label text-sm">Late Threshold</label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              className="form-input w-24"
              value={formData.attendanceRules.defaultLateThreshold}
              onChange={(e) => updateFormSection('attendanceRules', { 
                ...formData.attendanceRules, 
                defaultLateThreshold: parseInt(e.target.value) || 0 
              })}
              min="0"
            />
            <span className="text-gray-600">minutes after start</span>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="form-label text-sm">Auto-mark Absent</label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              className="form-input w-24"
              value={formData.attendanceRules.markAbsentAfterMinutes}
              onChange={(e) => updateFormSection('attendanceRules', { 
                ...formData.attendanceRules, 
                markAbsentAfterMinutes: parseInt(e.target.value) || 0 
              })}
              min="0"
            />
            <span className="text-gray-600">minutes after start</span>
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h4 className="font-medium text-gray-900 mb-4">Check-in Methods</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { key: 'allowQrCheckIn', label: 'Allow QR Code check-in' },
            { key: 'allowStaffCheckIn', label: 'Allow staff manual check-in' },
            { key: 'allowParentAppCheckIn', label: 'Allow parent app check-in' },
            { key: 'requirePhotoOnCheckIn', label: 'Require photo on check-in' },
            { key: 'allowEarlyCheckout', label: 'Allow early checkout' },
            { key: 'earlyCheckoutRequiresApproval', label: 'Early checkout requires approval' }
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={formData.attendanceRules[key]}
                onChange={(e) => updateFormSection('attendanceRules', { 
                  ...formData.attendanceRules, 
                  [key]: e.target.checked 
                })}
                className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
              />
              <span className="text-gray-700 text-sm">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="border-t pt-6">
        <h4 className="font-medium text-gray-900 mb-4">Parent Notifications</h4>
        <p className="text-sm text-gray-500 mb-3">Select when parents should be notified</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { key: 'notifyParentOnCheckIn', label: 'Notify on check-in' },
            { key: 'notifyParentOnCheckOut', label: 'Notify on check-out' },
            { key: 'notifyParentOnAbsence', label: 'Notify on absence' },
            { key: 'notifyParentOnLate', label: 'Notify when late' }
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={formData.attendanceRules[key]}
                onChange={(e) => updateFormSection('attendanceRules', { 
                  ...formData.attendanceRules, 
                  [key]: e.target.checked 
                })}
                className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
              />
              <span className="text-gray-700 text-sm">{label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPaymentPlans = () => (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Payment Plans</h3>
          <p className="text-sm text-gray-500">Create payment options for this class</p>
        </div>
        <button
          type="button"
          onClick={handleAddPaymentPlan}
          className="btn btn-secondary flex items-center text-sm"
        >
          <Plus size={16} className="mr-1" />
          Add Plan
        </button>
      </div>

      {errors.payments && errors.payments.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          {errors.payments.map((err, i) => (
            <p key={i} className="text-red-600 text-sm flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {err}
            </p>
          ))}
        </div>
      )}

      {formData.paymentPlans.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <DollarSign className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="font-medium">No payment plans added yet</p>
          <p className="text-sm mt-1 mb-3">Add pricing options for parents</p>
          <button
            type="button"
            onClick={handleAddPaymentPlan}
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            Add your first plan
          </button>
        </div>
      )}

      <div className="space-y-4">
        {formData.paymentPlans.map((plan, index) => (
          <div key={index} className={`bg-gray-50 rounded-lg p-4 border ${plan.isDefault ? 'border-purple-300 ring-1 ring-purple-200' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={plan.isDefault}
                    onChange={(e) => handlePaymentPlanChange(index, 'isDefault', e.target.checked)}
                    className="w-4 h-4 text-purple-600 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Default Plan</span>
                </label>
                {plan.isDefault && (
                  <span className="badge badge-primary text-xs">Recommended</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleRemovePaymentPlan(index)}
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Plan Name *</label>
                <input
                  type="text"
                  className="form-input text-sm"
                  value={plan.planName}
                  onChange={(e) => handlePaymentPlanChange(index, 'planName', e.target.value)}
                  placeholder="e.g., Monthly Plan"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    className="form-input text-sm pl-7"
                    value={plan.amount}
                    onChange={(e) => handlePaymentPlanChange(index, 'amount', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Billing Frequency</label>
                <select
                  className="form-input text-sm"
                  value={plan.billingFrequency}
                  onChange={(e) => handlePaymentPlanChange(index, 'billingFrequency', e.target.value)}
                >
                  {BILLING_FREQUENCIES.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Plan Type</label>
                <select
                  className="form-input text-sm"
                  value={plan.planType}
                  onChange={(e) => handlePaymentPlanChange(index, 'planType', e.target.value)}
                >
                  <option value="standard">Standard</option>
                  <option value="discounted">Discounted</option>
                  <option value="premium">Premium</option>
                  <option value="drop_in">Drop-in</option>
                  <option value="trial">Trial</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
              <input
                type="text"
                className="form-input text-sm"
                value={plan.description}
                onChange={(e) => handlePaymentPlanChange(index, 'description', e.target.value)}
                placeholder="Optional description of this plan"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Sibling Discount (%)</label>
                <input
                  type="number"
                  className="form-input text-sm"
                  value={plan.siblingDiscountPercent}
                  onChange={(e) => handlePaymentPlanChange(index, 'siblingDiscountPercent', parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Early Bird Discount (%)</label>
                <input
                  type="number"
                  className="form-input text-sm"
                  value={plan.earlyBirdDiscountPercent}
                  onChange={(e) => handlePaymentPlanChange(index, 'earlyBirdDiscountPercent', parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPrerequisites = () => (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Prerequisites</h3>
          <p className="text-sm text-gray-500">Require students to complete other classes first</p>
        </div>
        <button
          type="button"
          onClick={handleAddPrerequisite}
          className="btn btn-secondary flex items-center text-sm"
          disabled={availableClasses.filter(c => c.id !== classItem?.id).length === 0}
        >
          <Plus size={16} className="mr-1" />
          Add Prerequisite
        </button>
      </div>

      {formData.prerequisites.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Users className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="font-medium">No prerequisites added yet</p>
          <p className="text-sm mt-1 mb-3">Students can enroll without prior requirements</p>
          {availableClasses.filter(c => c.id !== classItem?.id).length > 0 && (
            <button
              type="button"
              onClick={handleAddPrerequisite}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Add your first prerequisite
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {formData.prerequisites.map((prereq, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium">
                {index + 1}
              </span>
              <button
                type="button"
                onClick={() => handleRemovePrerequisite(index)}
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Required Class *</label>
                <select
                  className="form-input"
                  value={prereq.prerequisiteClassId}
                  onChange={(e) => handlePrerequisiteChange(index, 'prerequisiteClassId', e.target.value)}
                >
                  <option value="">Select a class...</option>
                  {availableClasses
                    .filter(c => c.id !== classItem?.id)
                    .map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
              </div>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={prereq.isRequired}
                  onChange={(e) => handlePrerequisiteChange(index, 'isRequired', e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded border-gray-300"
                />
                <span className="text-gray-700 text-sm">Required (vs. recommended)</span>
              </label>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
                <input
                  type="text"
                  className="form-input text-sm"
                  value={prereq.notes}
                  onChange={(e) => handlePrerequisiteChange(index, 'notes', e.target.value)}
                  placeholder="e.g., Must complete with grade B or higher"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCustomFields = () => (
    <div className="space-y-5 animate-fade-in">
      <div>
        <h3 className="font-medium text-gray-900">Custom Fields</h3>
        <p className="text-sm text-gray-500">Additional information specific to this class</p>
      </div>

      {customFieldDefs.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Settings className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="font-medium">No custom fields defined</p>
          <p className="text-sm mt-1">
            Go to Settings → Custom Fields to create fields for your classes
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {customFieldDefs.map(field => (
            <div key={field.id} className="bg-gray-50 p-4 rounded-lg">
              <label className="form-label flex items-center">
                {field.field_label || field.fieldLabel}
                {field.is_required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.help_text && (
                <p className="text-xs text-gray-500 mb-2">{field.help_text}</p>
              )}

              {field.field_type === 'text' && (
                <input
                  type="text"
                  className="form-input"
                  value={formData.customFields[field.id] || ''}
                  onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                  required={field.is_required}
                  placeholder={field.placeholder || ''}
                />
              )}

              {field.field_type === 'textarea' && (
                <textarea
                  className="form-input"
                  rows="3"
                  value={formData.customFields[field.id] || ''}
                  onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                  required={field.is_required}
                  placeholder={field.placeholder || ''}
                />
              )}

              {field.field_type === 'number' && (
                <input
                  type="number"
                  className="form-input"
                  value={formData.customFields[field.id] || ''}
                  onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                  required={field.is_required}
                  placeholder={field.placeholder || ''}
                />
              )}

              {field.field_type === 'date' && (
                <input
                  type="date"
                  className="form-input"
                  value={formData.customFields[field.id] || ''}
                  onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                  required={field.is_required}
                />
              )}

              {field.field_type === 'select' && field.options && (
                <select
                  className="form-input"
                  value={formData.customFields[field.id] || ''}
                  onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                  required={field.is_required}
                >
                  <option value="">Select...</option>
                  {(typeof field.options === 'string' 
                    ? field.options.split(',').map(o => o.trim()) 
                    : field.options
                  ).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}

              {field.field_type === 'checkbox' && (
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.customFields[field.id] || false}
                    onChange={(e) => handleCustomFieldChange(field.id, e.target.checked)}
                    className="w-4 h-4 text-purple-600 rounded border-gray-300"
                  />
                  <span className="text-gray-700">Yes</span>
                </label>
              )}

              {field.field_type === 'boolean' && (
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`field-${field.id}`}
                      checked={formData.customFields[field.id] === true}
                      onChange={() => handleCustomFieldChange(field.id, true)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`field-${field.id}`}
                      checked={formData.customFields[field.id] === false}
                      onChange={() => handleCustomFieldChange(field.id, false)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-gray-700">No</span>
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {isEditing ? 'Edit Class' : 'Add New Class'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isEditing ? 'Update class details and settings' : 'Create a new class with full customization'}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 overflow-x-auto bg-gray-50/50">
          {TABS.map(tab => {
            const hasErrors = errors[tab.id];
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                } ${hasErrors ? 'text-red-500' : ''}`}
              >
                <tab.icon size={16} />
                <span>{tab.label}</span>
                {hasErrors && (
                  <AlertCircle size={14} className="text-red-500" />
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="class-form" onSubmit={handleSubmit}>
            {activeTab === 'basic' && renderBasicInfo()}
            {activeTab === 'schedule' && renderSchedule()}
            {activeTab === 'attendance' && renderAttendance()}
            {activeTab === 'payments' && renderPaymentPlans()}
            {activeTab === 'prerequisites' && renderPrerequisites()}
            {activeTab === 'custom' && renderCustomFields()}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={goToPrevTab}
              disabled={activeTab === TABS[0].id}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm text-gray-500">
              {TABS.findIndex(t => t.id === activeTab) + 1} of {TABS.length}
            </span>
            <button
              type="button"
              onClick={goToNextTab}
              disabled={activeTab === TABS[TABS.length - 1].id}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="flex items-center space-x-3">
            {errors.submit && (
              <span className="text-red-500 text-sm flex items-center">
                <AlertCircle size={14} className="mr-1" />
                {errors.submit}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="class-form"
              disabled={isLoading}
              className="btn btn-primary disabled:opacity-50 flex items-center"
            >
              {isLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle size={18} className="mr-2" />
                  {isEditing ? 'Save Changes' : 'Create Class'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassForm;
